import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

/**
 * Alert / Callout — a form- or page-level message in one of four variants.
 * `variant` is the one styling axis, matching Button/Badge/FormCard (audit
 * §F1); the danger term is `destructive` across all four. Tokens only: the
 * palette draws on the brand, muted, warning (ADR 071), and destructive
 * families, so the variants map as
 *   info        → neutral muted surface,
 *   success     → brand accent surface,
 *   warning     → warning family (`--warning-*` amber ramp),
 *   destructive → destructive ramp with `--error-text` message text.
 * Compose the root with `AlertTitle` and `AlertDescription`. `destructive` is
 * announced assertively (`role="alert"`); the calmer variants use
 * `role="status"`, and an explicit `role` prop always wins.
 *
 * No `size` axis by design (audit §F2): an alert always renders at the one
 * body-sm scale — its footprint is the message's, not a layout knob.
 */
const alertVariants = cva(
	'relative w-full rounded-lg border px-4 py-3 text-body-sm',
	{
		variants: {
			variant: {
				info: 'border-border bg-muted text-foreground',
				success: 'border-brand bg-brand-soft text-foreground',
				warning: 'border-warning-border bg-warning-soft text-warning-fg',
				destructive: 'border-destructive bg-destructive/10 text-error-text',
			},
		},
		defaultVariants: {
			variant: 'info',
		},
	},
)

export type AlertVariant = NonNullable<
	VariantProps<typeof alertVariants>['variant']
>

/** destructive is assertive; info/success/warning are polite status messages. */
const roleForVariant: Record<AlertVariant, 'alert' | 'status'> = {
	info: 'status',
	success: 'status',
	warning: 'status',
	destructive: 'alert',
}

function Alert({
	className,
	variant = 'info',
	role,
	...props
}: React.ComponentProps<'div'> & { variant?: AlertVariant }) {
	return (
		<div
			data-slot="alert"
			role={role ?? roleForVariant[variant]}
			className={cn(alertVariants({ variant }), className)}
			{...props}
		/>
	)
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="alert-title"
			className={cn('mb-1 leading-none font-medium', className)}
			{...props}
		/>
	)
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="alert-description"
			className={cn('text-body-sm [&_p]:leading-relaxed', className)}
			{...props}
		/>
	)
}

export { Alert, AlertTitle, AlertDescription, alertVariants }
